import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { BarChart3, List } from "lucide-react";
import { useTranslation } from "react-i18next";
import InfoHint from "../common/InfoHint";

const TABS = [
  { key: "timeline", labelKey: "session.chart.tabs.timeline" },
  { key: "magnitude", labelKey: "session.chart.tabs.magnitude" },
  { key: "load", labelKey: "session.chart.tabs.load" },
];

/**
 * Tabbed session charts. Every tab shares one vertical structure so switching
 * never shifts the layout:
 *
 *   tab bar → stat cards row → legend/controls row → 220px chart → footer slot
 *
 * Timeline / Magnitude render `statsContent` (impact stat cards) plus the
 * impact bar chart. Load renders `loadContent` (the session load graph, which
 * lays out its own rows to the same shape). The load content stays mounted
 * while another tab is open so it fetches once and reappears instantly.
 */
const SessionChart = ({
  timelineBuckets,
  magnitudeBuckets,
  selectedPlayerName,
  onViewImpactList,
  hasData,
  statsContent = null,
  loadContent = null,
  activeTab: controlledTab,
  onTabChange,
}) => {
  const { t } = useTranslation();
  const [internalTab, setInternalTab] = useState("timeline");
  const activeTab = controlledTab ?? internalTab;
  const setActiveTab = (key) => {
    if (onTabChange) onTabChange(key);
    if (controlledTab === undefined) setInternalTab(key);
  };

  const data = activeTab === "timeline" ? timelineBuckets : magnitudeBuckets;
  const hasSelectedPlayer = !!selectedPlayerName;
  const isLoadTab = activeTab === "load";

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className="min-w-[140px] rounded-lg border border-[#F3F4F6] bg-white px-3 py-2.5 shadow-lg">
        <p className="mb-1.5 text-[13px] font-semibold text-[#111827]">
          {label}
        </p>
        <div className="space-y-1.5 border-t border-[#F3F4F6] pt-1.5">
          {payload.map((entry) => (
            <div
              key={entry.dataKey}
              className="flex items-center justify-between gap-3"
            >
              <div className="flex items-center gap-1.5">
                <span
                  className="h-2 w-2 rounded"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-[11px] text-[#6B7280]">{entry.name}</span>
              </div>
              <span className="text-xs font-semibold text-[#111827]">
                {entry.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Tab Bar — always first, on every tab */}
      <div className="mb-4 flex items-center gap-1 rounded-lg bg-[#F3F4F6] p-0.5">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition duration-150 ${
              activeTab === tab.key
                ? "bg-white text-[#111827] shadow-sm"
                : "text-[#6B7280] hover:text-[#374151]"
            }`}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {/* Impact tabs */}
      <div hidden={isLoadTab}>
        {statsContent}

        {!hasData ? (
          <>
            {/* Legend slot kept for alignment with the data state */}
            <div className="mb-3 min-h-[28px]" />
            <div className="flex h-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-[#E5E7EB] bg-[#FAFAFA] text-center">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-xl bg-[#FEF3E7]">
                <BarChart3 size={28} className="text-[#E07A3A]" />
              </div>
              <h4 className="mb-1 text-sm font-semibold text-[#111827]">
                {t("session.chart.emptyTitle")}
              </h4>
              <p className="max-w-[240px] text-xs leading-relaxed text-[#6B7280]">
                {t("session.chart.emptyBody")}
              </p>
            </div>
            <div className="mt-4 h-[34px]" />
          </>
        ) : (
          <>
            {/* Legend row */}
            <div className="mb-3 flex min-h-[28px] items-center justify-end gap-4">
              <div className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded bg-[#E07A3A]" />
                <span className="text-xs text-[#6B7280]">
                  {t("session.chart.allPlayers")}
                </span>
              </div>
              {hasSelectedPlayer && (
                <div className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded bg-[#4F46E5]" />
                  <span className="text-xs text-[#6B7280]">
                    {selectedPlayerName}
                  </span>
                </div>
              )}
            </div>

            {/* Chart */}
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data}
                  margin={{ top: 5, right: 5, left: -20, bottom: 5 }}
                  barCategoryGap="20%"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#F3F4F6"
                  />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: "#9CA3AF" }}
                    dy={8}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: "#9CA3AF" }}
                    allowDecimals={false}
                    dx={-5}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "#F9FAFB" }}
                  />
                  <Bar
                    dataKey="allCount"
                    name={t("session.chart.allAthletes")}
                    fill="#E07A3A"
                    radius={[3, 3, 0, 0]}
                    maxBarSize={40}
                  />
                  {hasSelectedPlayer && (
                    <Bar
                      dataKey="selectedCount"
                      name={selectedPlayerName}
                      fill="#4F46E5"
                      radius={[3, 3, 0, 0]}
                      maxBarSize={40}
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Footer slot: View Impact List */}
            <div className="mt-4 flex h-[34px] items-center justify-center">
              <button
                onClick={onViewImpactList}
                className="flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] px-4 py-2 text-xs font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
              >
                <List size={14} />
                {t("session.chart.viewImpactList")}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Load tab — kept mounted so the graph is fetched once */}
      <div hidden={!isLoadTab}>
        <p className="mb-3 flex items-center gap-1.5 text-xs text-[#6B7280]">
          <InfoHint text={t("session.load.info")} />
          {t("session.load.infoShort")}
        </p>
        {loadContent}
      </div>
    </div>
  );
};

export default SessionChart;
